import axios, { AxiosResponse } from 'axios';
import { applicationInfo, commentData, } from '../types/interfaces';
import { DASHBOARD_PAGE_SIZE } from '../utils/Constants';
import { secureGet, securePatch, securePost, securePut } from '../utils/HttpUtils';

const apiUrls = {
  applications: '/v1/private/applications/',
  applicationsAll: '/v1/private/applications/all',
  clients: '/v1/private/clients/',
  dashboard: 'v1/private/stats',
};

export const sendApplication = async (
  cuit: string,
  application: applicationInfo,
  id: string
) => {
  try {
    if (Number(id) > 0) {
      const url = apiUrls.clients + cuit + '/applications/' + id;
      return securePut(url, application);
    } else {
      const url = apiUrls.clients + cuit + '/applications';
      return securePost(url, application);
    }
  } catch (error) {
    console.error('Error sending application:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateApplication = async (
  cuit: string,
  commentData: commentData,
  id: string
) => {
  try {
    const url = apiUrls.clients + cuit + '/applications/' + id;
    return securePatch(url, commentData);
  } catch (error) {
    console.error(
      'Error updating application:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const downloadApplicationFile = async (
  cuit: string,
  applicationId: string,
  fileName: string
) => {
  const token = localStorage.getItem('token');
  try {
    const response: AxiosResponse = await axios.get(
      `${apiUrls.clients}${cuit}/applications/${applicationId}/files/${fileName}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      }
    );
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    }
    return {
      success: false,
      message: 'An unexpected error occurred',
    };
  }
};

export const getApplication = async (cuit: string, id: string) => {
  try {
    const url = apiUrls.clients + cuit + '/applications/' + id;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching application:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApplications = async (
  page: number,
  statuses: string[] = [],
  text: string = '',
  filters?: Record<string, string>
) => {
  try {
    const statusesQueryParam = statuses
      .filter((status) => status.trim() !== '')
      .map((status) => `&status=${status}`)
      .join('');

    let url =
      apiUrls.applications +
      '?size=' +
      DASHBOARD_PAGE_SIZE +
      '&page=' +
      page +
      statusesQueryParam +
      (text ? '&filterText=' + text : '');

    // Agregar filtros adicionales si existen
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching applications:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getAllApplications = async (
  page: number,
  statuses: string[] = [],
  text: string = ''
) => {
  try {
    const statusesQueryParam = statuses
      .filter((status) => status.trim() !== '')
      .map((status) => `&status=${status}`)
      .join('');

    const url =
      apiUrls.applicationsAll +
      '?size=' +
      DASHBOARD_PAGE_SIZE +
      '&page=' +
      page +
      statusesQueryParam +
      (text ? '&filterText=' + text : '');
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching all applications:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApplicationsByCuit = async (cuit: string) => {
  try {
    return getApplications(0, [], cuit);
  } catch (error) {
    console.error(
      'Error fetching applications by CUIT:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getAllApplicationsByCuit = async (cuit: string) => {
  try {
    return getAllApplications(0, [], cuit);
  } catch (error) {
    console.error(
      'Error fetching all applications by CUIT:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApplicationResume = async () => {
  try {
    const url = apiUrls.dashboard;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching application resume:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const downloadSignerDocument = async (
  id: string | undefined,
  cuit: string | undefined,
  fileName: string | undefined
) => {
  const token = localStorage.getItem('token');
  try {
    const response: AxiosResponse = await axios.get(
      apiUrls.clients + cuit + '/applications/' + id + '/files/' + fileName,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      }
    );
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    } else {
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  }
};

export const deleteSignerDocument = async (
  id: string,
  cuit: string,
  fileName: string | undefined
) => {
  const token = localStorage.getItem('token');
  try {
    const response: AxiosResponse = await axios.delete(
      apiUrls.clients + cuit + '/applications/' + id + '/files/' + fileName,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      }
    );
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    } else {
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  }
};

export const deleteApplicationFile = async (
  cuit: string,
  idApplication: string,
  assetFilePath: string,
  fileType?: string
) => {
  const token = localStorage.getItem('token');
  try {
    const url =
      apiUrls.clients +
      cuit +
      '/applications/' +
      idApplication +
      '/files/' +
      assetFilePath;
    const response: AxiosResponse = await axios.delete(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: fileType ? { file_type: fileType } : {},
    });
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    } else {
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  }
};

export const getContracts = async (applicationId: string | undefined) => {
  try {
    const url = apiUrls.applications + applicationId + '/contracts'
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching contracts:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getFilters = async (referrerType?: 'application' | 'client') => {
  try {
    let url = '/v1/private/application/filters';
    if (referrerType) {
      url += `?referrer_type=${referrerType}`;
    }
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching filters:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getOtherLegalEntitiesFileTypes = async () => {
  try {
    const url = '/v1/private/applications/file-types/other-legal-entities';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching other legal entities file types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getFileTypes = async () => {
  try {
    const url = '/v1/private/applications/file-types';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching file types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};